import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import Button from '../../../components/ui/Button';
import { Checkbox } from '../../../components/ui/Checkbox';

const BookingForm = ({ selectedPermit, selectedDate, onSubmit }) => {
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    phone: '',
    nationality: '',
    groupSize: '1',
    accommodation: '',
    specialRequirements: '',
    emergencyContact: '',
    emergencyPhone: '',
    agreeTerms: false,
    agreeConservation: false
  });

  const [errors, setErrors] = useState({});

  const nationalityOptions = [
    { value: 'ph', label: 'Philippines' },
    { value: 'us', label: 'United States' },
    { value: 'uk', label: 'United Kingdom' },
    { value: 'au', label: 'Australia' },
    { value: 'jp', label: 'Japan' },
    { value: 'kr', label: 'South Korea' },
    { value: 'cn', label: 'China' },
    { value: 'other', label: 'Other' }
  ];

  const accommodationOptions = [
    { value: 'none', label: 'No accommodation needed' },
    { value: 'camping', label: 'Camping (Bring own tent)' },
    { value: 'ranger-station', label: 'Ranger Station Dormitory' },
    { value: 'local-homestay', label: 'Local Community Homestay' }
  ];

  const groupSizeOptions = Array.from({ length: 15 }, (_, i) => ({
    value: String(i + 1),
    label: `${i + 1} ${i === 0 ? 'person' : 'people'}`
  }));

  const handleInputChange = (e) => {
    const { name, value } = e?.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors?.[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleSelectChange = (name, value) => {
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors?.[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleCheckboxChange = (name, checked) => {
    setFormData(prev => ({ ...prev, [name]: checked }));
    if (errors?.[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData?.fullName?.trim()) newErrors.fullName = 'Full name is required';
    if (!formData?.email?.trim()) newErrors.email = 'Email is required';
    else if (!/\S+@\S+\.\S+/?.test(formData?.email)) newErrors.email = 'Invalid email format';
    if (!formData?.phone?.trim()) newErrors.phone = 'Phone number is required';
    if (!formData?.nationality) newErrors.nationality = 'Nationality is required';
    if (!formData?.emergencyContact?.trim()) newErrors.emergencyContact = 'Emergency contact is required';
    if (!formData?.emergencyPhone?.trim()) newErrors.emergencyPhone = 'Emergency phone is required';
    if (!formData?.agreeTerms) newErrors.agreeTerms = 'You must agree to terms and conditions';
    if (!formData?.agreeConservation) newErrors.agreeConservation = 'You must agree to conservation guidelines';

    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleSubmit = (e) => {
    e?.preventDefault();
    if (validateForm()) {
      onSubmit(formData);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="bg-[var(--color-card)] rounded-lg p-6 border border-[var(--color-border)]">
      <div className="flex items-center gap-3 mb-6">
        <div className="p-3 rounded-lg bg-[var(--color-primary)]">
          <Icon name="FileText" size={24} color="var(--color-primary-foreground)" />
        </div>
        <div>
          <h3 className="text-xl font-semibold text-[var(--color-foreground)]">Visitor Information</h3>
          <p className="text-sm text-[var(--color-muted-foreground)]">Complete your booking details</p>
        </div>
      </div>
      <div className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Input
            label="Full Name"
            type="text"
            name="fullName"
            placeholder="Enter your full name"
            value={formData?.fullName}
            onChange={handleInputChange}
            error={errors?.fullName}
            required
          />

          <Input
            label="Email Address"
            type="email"
            name="email"
            placeholder="your.email@example.com"
            value={formData?.email}
            onChange={handleInputChange}
            error={errors?.email}
            required
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Input
            label="Phone Number"
            type="tel"
            name="phone"
            placeholder="+63 XXX XXX XXXX"
            value={formData?.phone}
            onChange={handleInputChange}
            error={errors?.phone}
            required
          />

          <Select
            label="Nationality"
            options={nationalityOptions}
            value={formData?.nationality}
            onChange={(value) => handleSelectChange('nationality', value)}
            error={errors?.nationality}
            placeholder="Select your nationality"
            required
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Select
            label="Group Size"
            options={groupSizeOptions}
            value={formData?.groupSize}
            onChange={(value) => handleSelectChange('groupSize', value)}
            required
          />

          <Select
            label="Accommodation"
            options={accommodationOptions}
            value={formData?.accommodation}
            onChange={(value) => handleSelectChange('accommodation', value)}
            placeholder="Select accommodation type"
          />
        </div>

        <Input
          label="Special Requirements"
          type="text"
          name="specialRequirements"
          placeholder="Dietary restrictions, accessibility needs, etc."
          value={formData?.specialRequirements}
          onChange={handleInputChange}
          description="Optional: Let us know if you have any special needs"
        />

        <div className="pt-4 border-t border-[var(--color-border)]">
          <h4 className="font-medium text-[var(--color-foreground)] mb-4">Emergency Contact</h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Input
              label="Emergency Contact Name"
              type="text"
              name="emergencyContact"
              placeholder="Full name"
              value={formData?.emergencyContact}
              onChange={handleInputChange}
              error={errors?.emergencyContact}
              required
            />

            <Input
              label="Emergency Contact Phone"
              type="tel"
              name="emergencyPhone"
              placeholder="+63 XXX XXX XXXX"
              value={formData?.emergencyPhone}
              onChange={handleInputChange}
              error={errors?.emergencyPhone}
              required
            />
          </div>
        </div>

        <div className="pt-4 border-t border-[var(--color-border)] space-y-3">
          <Checkbox
            label="I agree to the terms and conditions, including park rules and visitor guidelines"
            checked={formData?.agreeTerms}
            onChange={(e) => handleCheckboxChange('agreeTerms', e?.target?.checked)}
            error={errors?.agreeTerms}
            required
          />

          <Checkbox
            label="I commit to following Leave No Trace principles and respecting the UNESCO World Heritage Site conservation guidelines"
            checked={formData?.agreeConservation}
            onChange={(e) => handleCheckboxChange('agreeConservation', e?.target?.checked)}
            error={errors?.agreeConservation}
            required
          />
        </div>

        <div className="pt-4 flex gap-3">
          <Button type="submit" variant="default" size="lg" className="flex-1">
            <Icon name="CreditCard" size={20} />
            Proceed to Payment
          </Button>
          <Button type="button" variant="outline" size="lg">
            <Icon name="Save" size={20} />
            Save Draft
          </Button>
        </div>
      </div>
    </form>
  );
};

export default BookingForm;